/**
 * FLIP (First, Last, Invert, Play) animation utilities for smooth card reordering
 */

export interface CardPosition {
	wireident: string;
	rect: DOMRect;
	element: HTMLElement;
}

/**
 * Capture current positions of all livewire cards
 */
export function captureCardPositions(): Map<string, CardPosition> {
	const positions = new Map<string, CardPosition>();

	const cardWrappers = document.querySelectorAll('.livewire-card-wrapper[data-wireident]');

	cardWrappers.forEach((element) => {
		const wireident = element.getAttribute('data-wireident');
		if (wireident && element instanceof HTMLElement) {
			const rect = element.getBoundingClientRect();
			positions.set(wireident, {
				wireident,
				rect,
				element
			});
		}
	});

	return positions;
}

/**
 * Animate cards from their old positions to new positions using FLIP technique
 */
export async function animateCardTransitions(
	oldPositions: Map<string, CardPosition>,
	duration: number = 500,
	stagger: number = 0
): Promise<void> {
	const cardWrappers = document.querySelectorAll('.livewire-card-wrapper[data-wireident]');
	const animations: Animation[] = [];

	cardWrappers.forEach((element, index) => {
		const wireident = element.getAttribute('data-wireident');
		if (!wireident || !(element instanceof HTMLElement)) return;

		const oldPosition = oldPositions.get(wireident);
		if (!oldPosition) return;

		const newRect = element.getBoundingClientRect();

		// Calculate the delta (how far the element needs to move)
		const deltaX = oldPosition.rect.left - newRect.left;

		// Skip animation if no movement needed (only check horizontal)
		if (Math.abs(deltaX) < 1) return;

		// Add animating class for z-index boost
		element.classList.add('animating');

		// Apply initial transform to move element back to old position (horizontal only)
		element.style.transform = `translateX(${deltaX}px)`;
		element.style.transition = 'none';

		// Force reflow
		element.offsetHeight;

		// Create animation to move to final position (horizontal slide only)
		const animation = element.animate([
			{
				transform: `translateX(${deltaX}px)`,
				opacity: '1'
			},
			{
				transform: 'translateX(0px)',
				opacity: '1'
			}
		], {
			duration: duration,
			delay: stagger * index,
			easing: 'cubic-bezier(0.4, 0, 0.2, 1)', // material design easing
			fill: 'both'
		});

		animations.push(animation);

		// Clean up after animation
		animation.addEventListener('finish', () => {
			element.style.transform = '';
			element.style.transition = '';
			element.classList.remove('animating');
		});
	});

	// Wait for all animations to complete
	if (animations.length > 0) {
		await Promise.all(animations.map(anim => anim.finished));
	}
}

/**
 * Smooth reordering function that combines position capture and animation
 */
export async function smoothReorder(reorderFn: () => void | Promise<void>): Promise<void> {
	// Capture positions before reordering
	const oldPositions = captureCardPositions();

	// Apply the reorder (this will trigger re-render)
	await reorderFn();

	// Wait for DOM to update
	await new Promise(resolve => setTimeout(resolve, 0));

	// Animate from old to new positions
	await animateCardTransitions(oldPositions, 350, 30);
}

/**
 * Add CSS classes needed for smooth transitions
 */
export function initializeCardAnimations(): void {
	const style = document.createElement('style');
	style.textContent = `
		.livewire-card-wrapper {
			position: relative;
			will-change: transform;
		}

		.livewire-card-wrapper.animating {
			z-index: 1000;
		}

		.livewire-card-wrapper.animating .card {
			background-color: white;
		}

		/* Fallback CSS transition for browsers that don't support Web Animations API */
		.livewire-card-wrapper.css-transition {
			transition: transform 350ms cubic-bezier(0.4, 0, 0.2, 1);
		}
	`;
	document.head.appendChild(style);
}